import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AddedToCart.css";

/**
 * Confirmation panel shown after an item is added to the cart.
 *
 * Replaces a toast that navigated the visitor to the cart page when it closed —
 * i.e. adding one item ended the browsing session. This shows what actually
 * went in, in the size and colour chosen, and lets them decide.
 *
 * Listens for a `riymay:added-to-cart` window event so any page can trigger it
 * without prop-drilling through the route tree.
 *
 * Motion: CSS transition, not keyframes. It can be fired twice in a second by
 * someone adding two items quickly, and a transition retargets from wherever it
 * is; keyframes would restart from zero and stutter.
 */

export const ADDED_EVENT = "riymay:added-to-cart";

/**
 * Fire from anywhere: emitAddedToCart({ name, image, size, color })
 * `imageSrcSet` is optional — a candidate list for the same photograph, so
 * the 68px thumbnail can fetch a 68px-worth copy instead of the original.
 */
export function emitAddedToCart(detail) {
  window.dispatchEvent(new CustomEvent(ADDED_EVENT, { detail }));
}

const DISMISS_AFTER = 5000;

const AddedToCart = () => {
  const [item, setItem] = useState(null);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const navigate = useNavigate();

  const close = () => {
    setOpen(false);
    // Keep the content mounted through the exit transition so it animates out
    // rather than vanishing.
    setTimeout(() => setItem(null), 260);
  };

  const startTimer = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(close, DISMISS_AFTER);
  };

  useEffect(() => {
    const onAdded = (e) => {
      // The panel is always mounted in its closed position (see the render
      // below), so there is no mount-then-animate race: setting open here
      // transitions from a position the browser has already painted.
      //
      // The earlier version rendered null until an item arrived, then waited a
      // frame with requestAnimationFrame before opening. rAF is throttled to
      // never firing in background and embedded contexts, which left the panel
      // mounted off-screen and invisible. Depending on nothing is better than
      // depending on a frame or a timer.
      setItem(e.detail || {});
      setOpen(true);
      startTimer();
    };
    window.addEventListener(ADDED_EVENT, onAdded);
    return () => {
      window.removeEventListener(ADDED_EVENT, onAdded);
      clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Always rendered, so the closed position is already painted. aria-hidden
  // and inert-by-pointer-events while closed, so it is invisible to both
  // assistive tech and the cursor until it actually has something to say.
  return (
    <div
      className={`atc ${open ? "atc--open" : ""}`}
      role="status"
      aria-live="polite"
      aria-hidden={open ? undefined : "true"}
      onMouseEnter={() => clearTimeout(timer.current)}
      onMouseLeave={startTimer}
    >
      <button className="atc__close" onClick={close} aria-label="Dismiss">&times;</button>

      <div className="atc__body">
        {item && item.image ? (
          /* The thumbnail is a flat 68px wide at every breakpoint
             (.atc__img in AddedToCart.css), so `sizes` is that one number.
             `imageSrcSet` is optional and null unless the emitter had a
             variant list to hand over; without it this is the plain <img>
             it has always been. */
          <img
            className="atc__img"
            src={item.image}
            alt=""
            {...(item.imageSrcSet
              ? { srcSet: item.imageSrcSet, sizes: "68px" }
              : {})}
          />
        ) : (
          <div className="atc__img atc__img--empty" />
        )}

        <div className="atc__text">
          <p className="atc__line">Smart choice.</p>
          {item && item.name && <p className="atc__name">{item.name}</p>}
          {item && (item.size || item.color) && (
            <p className="atc__meta">
              {[item.size, item.color].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
      </div>

      <div className="atc__actions">
        <button className="atc__btn atc__btn--primary" onClick={() => { close(); navigate("/cart"); }}>
          View cart
        </button>
        <button className="atc__btn" onClick={close}>
          Continue shopping
        </button>
      </div>
    </div>
  );
};

export default AddedToCart;
